'use client';

import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import {
  type EnvironmentSetting,
  EnvironmentSettingEnum,
} from '@/shared/constants';

export interface EnvironmentContentProps {
  value: EnvironmentSetting;
  onChange: (value: EnvironmentSetting) => void;
  // Layout variant — kept for API parity; both variants now use the same
  // Apple-grouped row idiom (light-gray card + title/description rows).
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
}

interface EnvironmentOption {
  id: EnvironmentSetting;
  name: string;
  description: string;
}

export function EnvironmentContent({
  value,
  onChange,
  closeOnSelect,
}: EnvironmentContentProps) {
  const t = useTranslations('render.settings.environment');
  const currentValue = value as string;

  const environmentOptions: EnvironmentOption[] = [
    {
      id: EnvironmentSettingEnum.NO_CHANGE,
      name: t('noChange'),
      description: t('keepExisting'),
    },
    {
      id: EnvironmentSettingEnum.CLEAR_DEBRIS,
      name: t('clearDebris'),
      description: t('removeClutter'),
    },
    {
      id: EnvironmentSettingEnum.SIMPLE_LAWN,
      name: t('simpleLawn'),
      description: t('basicLandscaping'),
    },
    {
      id: EnvironmentSettingEnum.FULL_LANDSCAPE,
      name: t('fullLandscape'),
      description: t('fullLandscaping'),
    },
  ];

  const handleChange = (val: string) => {
    onChange(val as EnvironmentSetting);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple `OptionalEnumPickerView` parity: single grouped card with
          title + description rows and a trailing checkmark. */}
      <SettingsCard>
        {environmentOptions.map((option) => (
          <PickerRow
            key={option.id}
            title={option.name}
            description={option.description}
            isSelected={currentValue === option.id}
            onClick={() => handleChange(option.id)}
          />
        ))}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getEnvironmentSubtitle(
  value: EnvironmentSetting,
  t: (key: string) => string
): string {
  const labels: Record<EnvironmentSetting, string> = {
    [EnvironmentSettingEnum.NO_CHANGE]: t('noChange'),
    [EnvironmentSettingEnum.CLEAR_DEBRIS]: t('clearDebris'),
    [EnvironmentSettingEnum.SIMPLE_LAWN]: t('simpleLawn'),
    [EnvironmentSettingEnum.FULL_LANDSCAPE]: t('fullLandscape'),
  };
  return labels[value] || t('noChange');
}
